import { useState, useEffect, useCallback, useMemo } from "react";
import { useApiQuery } from "@/api";
import { searchCompanies } from "./Company.queries";
import { CompanySearchResult } from "./Company.types";

export const useInfiniteScrollCompanies = (searchTerm: string) => {
    const [allCompanies, setAllCompanies] = useState<CompanySearchResult[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(true);

    useEffect(() => {
        setAllCompanies([]);
        setCurrentPage(1);
        setTotalResults(0);
        setTotalPages(0);
        setHasNextPage(true);
    }, [searchTerm]);

    const { data, isLoading, error, isSuccess } = useApiQuery(searchCompanies, {
        params: {
            query: searchTerm || "",
            page: currentPage,
            pageSize: 10,
        },
        enabled: currentPage > 0 && hasNextPage,
        key: [`companies-search-${searchTerm || "all"}-page-${currentPage}`],
    });

    useEffect(() => {
        if (isSuccess && data) {
            const { results, pagination } = data;

            setAllCompanies((prev) => {
                if (currentPage === 1) {
                    return results;
                } else {
                    return [...prev, ...results];
                }
            });

            setTotalResults(pagination.totalResults);
            setTotalPages(pagination.numPages);
            setHasNextPage(currentPage < pagination.numPages);
        }
    }, [data, isSuccess, currentPage]);

    const loadNextPage = useCallback(() => {
        if (!isLoading && hasNextPage && currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    }, [isLoading, hasNextPage, currentPage, totalPages]);

    const isInitialLoading = useMemo(() => {
        return isLoading && currentPage === 1;
    }, [isLoading, currentPage]);

    const isLoadingMore = useMemo(() => {
        return isLoading && currentPage > 1;
    }, [isLoading, currentPage]);

    return {
        companies: allCompanies,
        totalCount: totalResults,
        loadedCount: allCompanies.length,
        loadNextPage,
        hasNextPage,
        isLoading: isInitialLoading,
        isLoadingMore,
        error,
        pagination: {
            totalResults,
            totalPages,
            currentPage,
            pageSize: 10,
        },
    };
};
